import * as THREE from 'three'
import Experience from '../Experience.js'

export default class HitTesting {
    constructor() {
        this.experience = new Experience()
        this.renderer = this.experience.renderer
        this.scene = this.experience.scene
        this.camera = this.experience.camera

        // Hit test source for raycasting
        this.hitTestSource = null
        this.hitTestSourceRequested = false

        // Last hit position and orientation
        this.lastHitPosition = new THREE.Vector3()
        this.lastHitQuaternion = new THREE.Quaternion()
        this.hasValidHit = false

        // Reticle (visual indicator for placement)
        this.reticle = null
        this.createReticle()

        // Listen for XR session events
        this.setupSessionListeners()
    }

    createReticle() {
        // Simple ring reticle to show where model will be placed
        const geometry = new THREE.RingGeometry(0.1, 0.12, 32)
        const material = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            opacity: 0.7,
            transparent: true,
            side: THREE.DoubleSide
        })
        this.reticle = new THREE.Mesh(geometry, material)
        this.reticle.matrixAutoUpdate = false
        this.reticle.visible = false
        this.scene.add(this.reticle)
    }

    setupSessionListeners() {
        // We'll request hit test source when XR session starts
        this.renderer.instance.xr.addEventListener('sessionstart', async () => {
            const session = this.renderer.instance.xr.getSession()

            // Request hit test source
            if (session && 'requestHitTestSource' in session) {
                try {
                    const referenceSpace = await session.requestReferenceSpace('viewer')
                    this.hitTestSource = await session.requestHitTestSource({ space: referenceSpace })
                    this.hitTestSourceRequested = true
                } catch (error) {
                    console.warn('Hit testing not supported:', error)
                }
            }
        })

        this.renderer.instance.xr.addEventListener('sessionend', () => {
            this.hitTestSourceRequested = false
            this.hitTestSource = null
            this.hasValidHit = false
            if (this.reticle) this.reticle.visible = false
        })
    }

    update(frame) {
        // Only do hit testing in XR mode
        if (!this.renderer.instance.xr.isPresenting) {
            if (this.reticle) this.reticle.visible = false
            return
        }

        // If hand tracking is active, hide reticle and don't do hit testing
        if (this.experience.handTracking?.isAnyHandTracked()) {
            if (this.reticle) this.reticle.visible = false
            return
        }

        // Perform hit test
        if (frame && this.hitTestSource) {
            const referenceSpace = this.renderer.instance.xr.getReferenceSpace()
            const hitTestResults = frame.getHitTestResults(this.hitTestSource)

            if (hitTestResults.length > 0) {
                const hit = hitTestResults[0]
                const pose = hit.getPose(referenceSpace)

                if (pose) {
                    // Update reticle position
                    this.reticle.visible = true
                    this.reticle.matrix.fromArray(pose.transform.matrix)

                    // Store hit position and orientation
                    this.lastHitPosition.setFromMatrixPosition(this.reticle.matrix)
                    this.lastHitQuaternion.setFromRotationMatrix(this.reticle.matrix)
                    this.hasValidHit = true
                }
            } else {
                this.reticle.visible = false
            }
        }
    }

    getHitPosition() {
        return this.hasValidHit ? this.lastHitPosition.clone() : null
    }

    getHitQuaternion() {
        return this.hasValidHit ? this.lastHitQuaternion.clone() : null
    }

    hasHit() {
        return this.hasValidHit
    }
}
